import { InputState, Rectangle } from '../../types/game.types';
import { BaseGame } from '../BaseGame';
import { RenderManager } from '../../core/RenderManager';
import { AudioManager } from '../../core/AudioManager';
import { Player } from './entities/Player';
import { Enemy } from './entities/Enemy';
import { Bullet } from './entities/Bullet';
import { TunnelSystem } from './systems/TunnelSystem';

export class TunnelBlasterGame extends BaseGame {
  private player: Player | null = null;
  private enemies: Enemy[] = [];
  private bullets: Bullet[] = [];
  private tunnelSystem: TunnelSystem;
  
  // Game state
  private gameStarted: boolean = false;
  private tunnelSpeed: number = 2; // Speed of tunnel movement
  private enemySpawnTimer: number = 0;
  private readonly ENEMY_SPAWN_INTERVAL = 1.5; // Seconds between enemy spawns
  
  // Superzapper system
  private superzappers: number = 2; // Start with 2 per life
  private superzapperCooldown: number = 0;
  private readonly SUPERZAPPER_COOLDOWN = 1.0; // 1 second cooldown after use
  
  // Game constants
  private readonly TUNNEL_SEGMENTS = 20;
  private readonly TUNNEL_RADIUS = 100;
  private readonly MAX_ENEMIES = 6;

  constructor(renderManager: RenderManager, audioManager: AudioManager) {
    const gameConfig = { width: 900, height: 600, fps: 60, scale: 1 }; // Landscape for mobile
    super(renderManager, audioManager, gameConfig, 'tunnel-blaster');
    
    this.tunnelSystem = new TunnelSystem(
      this.TUNNEL_SEGMENTS,
      this.TUNNEL_RADIUS,
      renderManager
    );
  }

  public init(): void {
    console.log('Tunnel Blaster Game initialized');
    
    // Reset game state
    this.score = 0;
    this.lives = 3;
    this.level = 1;
    this.isGameOver = false;
    this.gameStarted = true;
    
    // Reset tunnel and entities
    this.tunnelSpeed = 2 + (this.level - 1) * 0.5;
    this.enemies = [];
    this.bullets = [];
    this.enemySpawnTimer = 0;
    
    // Create player ship
    this.createPlayer();
    
    // Initialize tunnel system
    this.tunnelSystem.reset();
  }

  private createPlayer(): void {
    const currentShape = this.tunnelSystem.getCurrentShape();
    this.player = new Player(
      'player',
      this.config.width / 2,
      this.config.height / 2,
      this.renderManager,
      this.audioManager,
      currentShape
    );
  }

  public update(deltaTime: number): void {
    if (this.isGameOver || !this.gameStarted || !this.player) return;

    // Update tunnel system
    this.tunnelSystem.update(deltaTime, this.tunnelSpeed);
    
    // Update player
    this.player.update(deltaTime);
    
    // Update bullets
    this.updateBullets(deltaTime);
    
    // Update enemies
    this.updateEnemies(deltaTime);
    
    // Spawn new enemies
    this.spawnEnemies(deltaTime);
    
    // Check collisions
    this.checkCollisions();
    
    // Check for level progression
    this.checkLevelProgression();
  }

  private updateBullets(deltaTime: number): void {
    this.bullets.forEach(bullet => {
      bullet.update(deltaTime);
    });
    
    // Remove bullets that are off screen
    this.bullets = this.bullets.filter(bullet => {
      const pos = bullet.getPosition();
      return pos.y > 0 && pos.y < this.config.height &&
             pos.x > 0 && pos.x < this.config.width;
    });
  }

  private updateEnemies(deltaTime: number): void {
    this.enemies.forEach(enemy => {
      enemy.updateWithTunnelSpeed(deltaTime, this.tunnelSpeed);
    });
    
    // Remove enemies that have passed the player or are destroyed
    this.enemies = this.enemies.filter(enemy => {
      return enemy.active && enemy.getDepth() > -1;
    });
  }

  private spawnEnemies(deltaTime: number): void {
    if (this.enemies.length >= this.MAX_ENEMIES) return;
    
    this.enemySpawnTimer -= deltaTime;
    if (this.enemySpawnTimer <= 0) {
      this.spawnEnemy();
      this.enemySpawnTimer = this.ENEMY_SPAWN_INTERVAL - (this.level * 0.1);
    }
  }

  private spawnEnemy(): void {
    // Spawn enemy at random position on tunnel perimeter at far end
    const angle = Math.random() * Math.PI * 2;
    const distance = this.TUNNEL_RADIUS * (0.6 + Math.random() * 0.3); // 60-90% of tunnel radius
    
    const enemy = new Enemy(
      `enemy_${Date.now()}`,
      angle,
      distance,
      1.0, // Start at far end of tunnel (depth 1.0)
      this.renderManager,
      this.audioManager
    );
    
    this.enemies.push(enemy);
  }

  private checkCollisions(): void {
    if (!this.player) return;
    
    const playerBounds = this.player.getBounds();
    
    // Check bullet-enemy collisions
    this.bullets.forEach((bullet, bulletIndex) => {
      this.enemies.forEach((enemy, enemyIndex) => {
        if (!enemy.active) return;
        
        const bulletPos = bullet.getPosition();
        const enemyPos = enemy.getScreenPosition(this.config.width, this.config.height);
        
        // Simple distance-based collision for 3D effect
        const distance = Math.sqrt(
          Math.pow(bulletPos.x - enemyPos.x, 2) + 
          Math.pow(bulletPos.y - enemyPos.y, 2)
        );
        
        if (distance < enemy.getSize() + 5) {
          // Hit!
          enemy.destroy();
          this.bullets.splice(bulletIndex, 1);
          
          // Tempest-style scoring
          const enemyType = enemy.getEnemyType();
          let points = 100; // Default
          switch (enemyType) {
            case 'flipper': points = 150; break;
            case 'tanker': points = 100; break;
            case 'spiker': points = 200; break;
            case 'spike': points = 50; break;
            case 'fuseball': points = 750; break;
            case 'pulsar': points = 1000; break;
          }
          
          this.addScore(points * this.level);
          this.audioManager.playExplosion();
        }
      });
    });
    
    // Check player-enemy collisions
    this.enemies.forEach(enemy => {
      if (!enemy.active || enemy.getDepth() > 0.1) return; // Only check close enemies
      
      const enemyPos = enemy.getScreenPosition(this.config.width, this.config.height);
      const distance = Math.sqrt(
        Math.pow(playerBounds.x + playerBounds.width/2 - enemyPos.x, 2) + 
        Math.pow(playerBounds.y + playerBounds.height/2 - enemyPos.y, 2)
      );
      
      if (distance < enemy.getSize() + this.player!.getSize()) {
        this.loseLife();
      }
    });
  }

  private checkLevelProgression(): void {
    // Progress to next level every 1000 points
    const targetLevel = Math.floor(this.score / 1000) + 1;
    if (targetLevel > this.level) {
      this.nextLevel();
      this.tunnelSpeed = 2 + (this.level - 1) * 0.5;
      // Reset tunnel with new shape for new level
      this.tunnelSystem.reset();
      // Update player to follow new shape
      if (this.player) {
        this.player.setShape(this.tunnelSystem.getCurrentShape());
      }
      console.log(`Level ${this.level}! New playfield shape! Tunnel speed: ${this.tunnelSpeed}`);
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Clear screen
    this.renderManager.clear('#000000');
    
    // Render tunnel
    this.tunnelSystem.render(this.config.width, this.config.height);
    
    // Render enemies (back to front)
    const sortedEnemies = [...this.enemies].sort((a, b) => b.getDepth() - a.getDepth());
    sortedEnemies.forEach(enemy => {
      if (enemy.active) {
        enemy.renderWithDimensions(ctx, this.config.width, this.config.height);
      }
    });
    
    // Render bullets
    this.bullets.forEach(bullet => {
      bullet.render(ctx);
    });
    
    // Render player (always on top)
    if (this.player) {
      this.player.render(ctx);
    }
    
    // Render HUD
    this.renderHUD();
    
    // Render game over screen
    if (this.isGameOver) {
      this.renderGameOver();
    }
    
    // Render initial entry if showing
    if (this.showingInitialEntry && this.initialEntry) {
      this.initialEntry.render(ctx, this.config.width / 2, this.config.height / 2);
    }
  }

  protected renderHUD(): void {
    // Score
    this.renderManager.drawText(
      `SCORE: ${this.score.toString().padStart(6, '0')}`,
      20,
      20,
      '#FFFFFF',
      '16px "Courier New", monospace'
    );
    
    // Lives
    this.renderManager.drawText('LIVES: ', 20, 50, '#FFFFFF', '16px "Courier New", monospace');
    for (let i = 0; i < this.lives; i++) {
      this.renderManager.drawC64Char('▲', 100 + i * 25, 48, '#00FF00', 1);
    }
    
    // Level
    this.renderManager.drawText(
      `LEVEL: ${this.level}`,
      this.config.width - 150,
      20,
      '#FFFFFF',
      '16px "Courier New", monospace'
    );
    
    // Speed indicator
    this.renderManager.drawText(
      `SPEED: ${this.tunnelSpeed.toFixed(1)}`,
      this.config.width - 150,
      50,
      '#CCCCCC',
      '12px "Courier New", monospace'
    );
    
    // Controls
    this.renderManager.drawText(
      'CONTROLS: A/D or Left/Right = Rotate, Space = Fire',
      20,
      this.config.height - 20,
      '#CCCCCC',
      '12px "Courier New", monospace'
    );
  }

  protected renderGameOver(): void {
    const centerX = this.config.width / 2;
    const centerY = this.config.height / 2;
    
    this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.7)');
    
    this.renderManager.drawTextCentered(
      'GAME OVER',
      centerX,
      centerY - 60,
      '#FFFFFF',
      '32px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      `FINAL SCORE: ${this.score}`,
      centerX,
      centerY - 20,
      '#FFFFFF',
      '18px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      `REACHED LEVEL ${this.level}`,
      centerX,
      centerY + 10,
      '#CCCCCC',
      '16px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      'PRESS SPACE TO RESTART',
      centerX,
      centerY + 60,
      '#FFFFFF',
      '14px "Courier New", monospace'
    );
  }

  public handleInput(input: InputState): void {
    if (this.isGameOver) {
      if (this.showingInitialEntry) {
        return;
      }
      
      if (input.keys['Space'] || input.touch.gesture === 'tap') {
        this.restart();
      }
      return;
    }

    if (!this.player) return;

    // Player movement (Tempest style - only left/right around rim)
    let moveX = 0;
    
    if (input.keys['ArrowLeft'] || input.keys['KeyA']) {
      moveX = -1;
    }
    if (input.keys['ArrowRight'] || input.keys['KeyD']) {
      moveX = 1;
    }
    
    this.player.setMovement(moveX, 0);
    
    // Shooting
    if (input.keys['Space'] && this.player.canFire()) {
      this.fireBullet();
    }
  }

  private fireBullet(): void {
    if (!this.player) return;
    
    const playerPos = this.player.getPosition();
    const rimAngle = this.player.getRimAngle();
    
    // Calculate direction toward tunnel center
    const directionX = Math.cos(rimAngle + Math.PI); // Opposite of rim angle
    const directionY = Math.sin(rimAngle + Math.PI);
    
    const bullet = new Bullet(
      `bullet_${Date.now()}`,
      playerPos.x,
      playerPos.y,
      directionX, // Fire toward center
      directionY, // Fire toward center
      this.renderManager,
      this.audioManager
    );
    
    this.bullets.push(bullet);
    this.player.resetFireTimer();
    this.audioManager.playShoot();
  }

  public loseLife(): void {
    this.lives--;
    if (this.lives <= 0) {
      this.gameOver();
    } else {
      console.log(`Life lost! ${this.lives} remaining`);
      // Reset Superzappers for new life (like original Tempest)
      this.superzappers = 2;
      this.superzapperCooldown = 0;
    }
  }
  
  public cleanup(): void {
    super.cleanup();
    this.enemies = [];
    this.bullets = [];
    this.player = null;
  }
}